import React, { useState, useEffect, useCallback } from 'react';
import { gtplSaathiAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { ShoppingCart, CheckCircle, XCircle, Search, Calculator, ListChecks, ClipboardList, Filter } from 'lucide-react';
import useDarkReady from '../theme/useDarkReady';
import { Screen, PageHead, Panel, Field, Notice, Tabs, Item, Pill, Empty, Loading } from '../components/gtpl/GtplKit';

const NO_PERMISSION = <span className="gx-hint" style={{ color: 'var(--g-bad)', margin: 0 }}>You don't have permission to renew on GTPL Saathi.</span>;

// Entity-wise browser — mirrors gtplsaathi.com's own MultiRenew.aspx screen: pick an Entity +
// due-date filter (+ optionally narrow to one day), get back a checklist of matching
// subscribers, tick the ones to renew. See gtplSaathiService.multiRenewSearch/Submit for how
// this replays GTPL's own search/renew flow server-side.
function EntityBrowser() {
  const { hasModuleRight } = useAuth();
  const canRenew = hasModuleRight('gtplSaathi', 'renew');

  const [loadingOptions, setLoadingOptions] = useState(true);
  const [entities, setEntities] = useState([]);
  const [filters, setFilters] = useState([]);
  const [validityOptions, setValidityOptions] = useState([]);

  const [entity, setEntity] = useState('');
  const [filterType, setFilterType] = useState('');
  const [date, setDate] = useState('1');
  const [dateOptions, setDateOptions] = useState([]);
  const [loadingDates, setLoadingDates] = useState(false);
  const [validity, setValidity] = useState('');

  const [searching, setSearching] = useState(false);
  const [rows, setRows] = useState(null);
  const [hasMorePages, setHasMorePages] = useState(false);
  const [checked, setChecked] = useState({});

  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const [calculating, setCalculating] = useState(false);
  const [calcAmount, setCalcAmount] = useState(null);

  useEffect(() => {
    gtplSaathiAPI.multiRenewEntities()
      .then(({ data }) => {
        setEntities(data.data.entities || []);
        setFilters(data.data.filters || []);
        setValidityOptions(data.data.validityOptions || []);
        if (data.data.entities?.length) setEntity(data.data.entities[0]);
        if (data.data.filters?.length) setFilterType(data.data.filters[0].value);
      })
      .catch((err) => toast.error(err.response?.data?.message || 'Failed to load GTPL entities'))
      .finally(() => setLoadingOptions(false));
  }, []);

  // Fetches the live Date options as soon as a filter is picked — same as gtplsaathi.com
  // itself, where the Date box appears immediately after choosing the due-date filter, before
  // Entity or Search are even touched. "Expired in Last 5 Days" genuinely has none (GTPL
  // doesn't offer date narrowing for that filter) — dateOptions just comes back empty then,
  // and the Date select stays hidden, which is correct rather than a bug.
  useEffect(() => {
    if (!filterType) return;
    setLoadingDates(true);
    setDateOptions([]);
    setDate('1');
    gtplSaathiAPI.multiRenewDates(filterType)
      .then(({ data }) => setDateOptions(data.data.dateOptions || []))
      .catch((err) => toast.error(err.response?.data?.message || 'Failed to load date options'))
      .finally(() => setLoadingDates(false));
  }, [filterType]);

  const runSearch = useCallback(async (dateVal) => {
    if (!entity || !filterType) return;
    setSearching(true);
    setRows(null);
    setResult(null);
    setChecked({});
    setCalcAmount(null);
    try {
      const { data } = await gtplSaathiAPI.multiRenewSearch({ entity, filterType, date: dateVal });
      setRows(data.data.rows);
      setHasMorePages(data.data.hasMorePages);
      if (!data.data.rows.length) toast('No matching subscribers for this Entity/filter', { icon: 'ℹ️' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Search failed');
    } finally {
      setSearching(false);
    }
  }, [entity, filterType]);

  const handleSearch = (e) => {
    e.preventDefault();
    runSearch(date);
  };

  const handleDateChange = (val) => {
    setDate(val);
    runSearch(val);
  };

  const toggleRow = (accountNumber) => {
    setChecked((c) => ({ ...c, [accountNumber]: !c[accountNumber] }));
    setCalcAmount(null);
  };
  const toggleAll = (next) => {
    const map = {};
    (rows || []).forEach((r) => { map[r.accountNumber] = next; });
    setChecked(map);
    setCalcAmount(null);
  };
  const selectedAccounts = Object.keys(checked).filter((k) => checked[k]);
  const allChecked = rows?.length > 0 && selectedAccounts.length === rows.length;

  const handleCalculateAmount = async () => {
    if (!selectedAccounts.length) { toast.error('Select at least one subscriber'); return; }
    setCalculating(true);
    setCalcAmount(null);
    try {
      const { data } = await gtplSaathiAPI.multiRenewCalculateAmount({ entity, filterType, date, accountNumbers: selectedAccounts });
      setCalcAmount({ ok: true, ...data.data });
    } catch (err) {
      const message = err.response?.data?.message || 'Calculate amount failed';
      setCalcAmount({ ok: false, message });
      toast.error(message);
    } finally {
      setCalculating(false);
    }
  };

  const handleRenewSelected = async () => {
    if (!selectedAccounts.length) { toast.error('Select at least one subscriber'); return; }
    setSubmitting(true);
    setResult(null);
    try {
      const { data } = await gtplSaathiAPI.multiRenewSubmit({
        entity, filterType, date, validity: validity || undefined, accountNumbers: selectedAccounts,
      });
      setResult({ ok: true, ...data.data });
      toast.success(`${data.data.renewedCount} renewed${data.data.notFoundCount ? `, ${data.data.notFoundCount} skipped (no longer listed)` : ''}`);
    } catch (err) {
      const message = err.response?.data?.message || 'Renew failed';
      setResult({ ok: false, message });
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingOptions) return <Loading />;

  return (
    <>
      <Panel icon={Filter} tone="info" title="1. Who is due?" sub="Same filters as gtplsaathi.com’s Multi Renew screen">
        <form onSubmit={handleSearch} className="gx-form">
          <div className="gx-grid">
            <Field label="Entity">
              <select className="form-control" value={entity} onChange={(e) => setEntity(e.target.value)}>
                {entities.map((e) => <option key={e} value={e}>{e}</option>)}
              </select>
            </Field>
            <Field label="Filter">
              <select className="form-control" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                {filters.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
            </Field>
            {loadingDates && <Field label="Date"><div className="gx-hint" style={{ padding: '9px 0', margin: 0 }}>Loading…</div></Field>}
            {!loadingDates && dateOptions.length > 0 && (
              <Field label="Date">
                <select className="form-control" value={date} onChange={(e) => handleDateChange(e.target.value)}>
                  {dateOptions.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
              </Field>
            )}
            <Field label="Validity (optional)">
              <select className="form-control" value={validity} onChange={(e) => setValidity(e.target.value)}>
                <option value="">GTPL default</option>
                {validityOptions.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
              </select>
            </Field>
          </div>
          <div className="gx-actions">
            <button type="submit" className="btn btn-primary" disabled={searching || !canRenew}>
              <Search size={15} />{searching ? 'Searching…' : 'Search'}
            </button>
            {!canRenew && NO_PERMISSION}
          </div>
        </form>
      </Panel>

      {rows && (
        <Panel icon={ListChecks} tone="ok" title="2. Tick who to renew" count={rows.length}
          sub={`${selectedAccounts.length} selected`}
          right={rows.length > 0 && (
            <label className="gx-actions" style={{ gap: 6, cursor: 'pointer', fontSize: '.82rem', fontWeight: 600 }}>
              <input type="checkbox" checked={allChecked} onChange={(e) => toggleAll(e.target.checked)} /> All
            </label>)}>
          {rows.length === 0 ? (
            <Empty title="No matching subscribers">Try another filter or date.</Empty>
          ) : (
            <>
              {hasMorePages && (
                <Notice tone="warn">GTPL has more results on a next page — only the first {rows.length} are shown here.</Notice>
              )}

              <div className="gx-list" style={{ maxHeight: 460, overflowY: 'auto', marginBottom: 4 }}>
                {rows.map((r) => (
                  <label key={r.accountNumber} style={{ display: 'block', cursor: 'pointer' }}>
                    <Item selected={!!checked[r.accountNumber]}
                      left={<input type="checkbox" checked={!!checked[r.accountNumber]} onChange={() => toggleRow(r.accountNumber)} style={{ marginTop: 3 }} />}
                      title={r.name} sub={`Account ${r.accountNumber}`}
                      right={<Pill tone="warn">{r.endDate}</Pill>}
                      meta={<>
                        <span><b>{r.package}</b></span>
                        <span>STB {r.stb}</span>
                        <span>{r.startDate} → {r.endDate}</span>
                        {(r.address || r.mobile) && <span>{r.address}{r.mobile ? ` · ${r.mobile}` : ''}</span>}
                      </>} />
                  </label>
                ))}
              </div>

              <div className="gx-sticky">
                <button type="button" className="btn btn-secondary" onClick={handleCalculateAmount} disabled={calculating || !selectedAccounts.length || !canRenew}>
                  <Calculator size={15} />{calculating ? 'Calculating…' : 'Calculate MSO amount'}
                </button>
                <button type="button" className="btn btn-success" onClick={handleRenewSelected} disabled={submitting || !selectedAccounts.length || !canRenew}>
                  <ShoppingCart size={15} />{submitting ? 'Renewing…' : `Renew ${selectedAccounts.length || ''} selected`}
                </button>
              </div>

              {calcAmount && (
                <div style={{ marginTop: 14 }}>
                  {calcAmount.ok === false ? (
                    <Notice tone="bad">{calcAmount.message}</Notice>
                  ) : (
                    <Notice tone="info" icon={Calculator} title="Total selected">
                      {calcAmount.message || `Rs ${calcAmount.amount ?? '—'}`}
                      {calcAmount.notFoundCount ? ` (${calcAmount.notFoundCount} no longer listed)` : ''}
                    </Notice>
                  )}
                </div>
              )}

              {result && (
                <Notice tone={result.ok === false ? 'bad' : 'ok'} icon={result.ok === false ? XCircle : CheckCircle}
                  title={result.ok === false ? 'Failed' : 'Submitted'}>
                  {result.message}
                </Notice>
              )}
            </>
          )}
        </Panel>
      )}
    </>
  );
}

// Secondary path — a hand-typed list of specific STB serials/account numbers, for renewing
// items a due-date filter above wouldn't surface. Loops the single Renew flow per item
// server-side (see gtplSaathiService.multiRenew).
function ListRenewer() {
  const { hasModuleRight } = useAuth();
  const canRenew = hasModuleRight('gtplSaathi', 'renew');

  const [list, setList] = useState('');
  const [validity, setValidity] = useState('');
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(null);

  const parseItems = () => list.split('\n').map((l) => l.trim()).filter(Boolean).map((line) => (
    /^\d+$/.test(line) && line.length <= 8 ? { accountNumber: line, label: line } : { stb: line, label: line }
  ));

  const handleRun = async () => {
    const items = parseItems();
    if (!items.length) { toast.error('Enter at least one STB serial or account number'); return; }
    if (items.length > 100) { toast.error('Max 100 at a time'); return; }
    setRunning(true);
    setResults(null);
    try {
      const { data } = await gtplSaathiAPI.multiRenew(items, validity || undefined);
      setResults(data.data);
      const failed = data.data.filter((r) => !r.ok).length;
      if (failed === 0) toast.success(`All ${data.data.length} renewed`);
      else toast.error(`${failed} of ${data.data.length} failed`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Multi renew failed');
    } finally {
      setRunning(false);
    }
  };

  const count = parseItems().length;

  return (
    <>
      <Panel icon={ClipboardList} tone="info" title="Paste a list" sub="One STB serial or account number per line">
        <p className="gx-hint" style={{ margin: '0 0 12px' }}>
          For STB serials / account numbers the due-date list wouldn't show. Runs one at a time against
          gtplsaathi.com, so a long list can take a while. Up to 100.
        </p>
        <div className="gx-form">
          <textarea className="form-control font-mono" rows={7} placeholder={'2920139673\n1005551\n...'}
            value={list} onChange={(e) => setList(e.target.value)} style={{ fontSize: '.84rem' }} />
          <Field label="Validity (optional)" style={{ maxWidth: 320 }}>
            <input className="form-control" value={validity} onChange={(e) => setValidity(e.target.value)} placeholder="Blank = each package's default" />
          </Field>
        </div>
        <div className="gx-sticky">
          <button type="button" className="btn btn-success" onClick={handleRun} disabled={running || !canRenew || !count}>
            <ShoppingCart size={15} />{running ? 'Renewing…' : `Renew ${count || ''} ${count === 1 ? 'item' : 'items'}`}
          </button>
          {!canRenew && NO_PERMISSION}
        </div>
      </Panel>

      {results && (
        <Panel icon={ListChecks} tone={results.every((r) => r.ok) ? 'ok' : 'bad'} title="Results" count={results.length}
          sub={`${results.filter((r) => r.ok).length} renewed · ${results.filter((r) => !r.ok).length} failed`}>
          <div className="gx-list">
            {results.map((r, i) => (
              <Item key={i} tone={r.ok ? 'ok' : 'bad'} title={r.input} sub={r.message}
                right={<Pill tone={r.ok ? 'ok' : 'bad'} dot>{r.ok ? 'Renewed' : 'Failed'}</Pill>} />
            ))}
          </div>
        </Panel>
      )}
    </>
  );
}

export default function GtplSaathiMultiRenewPage() {
  useDarkReady();
  const [tab, setTab] = useState('due');
  return (
    <Screen narrow>
      <PageHead icon={ListChecks} tone="info" title="Renew several customers"
        sub="Pick who is due, or paste a list of box numbers, and renew them in one go." />
      <Tabs value={tab} onChange={setTab} tabs={[
        { key: 'due', label: 'From the due list', icon: Filter },
        { key: 'list', label: 'Paste a list', icon: ClipboardList },
      ]} />
      {tab === 'due' ? <EntityBrowser /> : <ListRenewer />}
    </Screen>
  );
}
